import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { PublicNav } from "@/components/PublicNav";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";

const EASE = [0.22, 1, 0.36, 1];

const COMMITMENTS = [
  {
    heading: "No contracts.",
    body: "Cancel anytime. No notice period, no exit fees, no \u201Clet me transfer you to retention.\u201D Just click cancel.",
  },
  {
    heading: "No late fees. No collections. No threats.",
    body: "If you have a rough month and can't pay, just let us know. We'll pick things up next month. We've all been there before.",
  },
  {
    heading: "No hidden charges.",
    body: "No implementation fees. No upgrade fees. No service fees. The price you see is the price you pay.",
  },
  {
    heading: "Price locked in for life.",
    body: "If we ever raise prices, you stay grandfathered at the rate you signed up at. No surprises. Ever.",
  },
  {
    heading: "Fully reversible.",
    body: "Decide it's not for you? Switch back to your old tools the same day. No data lock-in, no penalty.",
  },
];

export default function Promise() {
  return (
    <div className="relative min-h-screen overflow-hidden bg-[#FAF8F5] text-slate-900 dark:bg-[#0b1220] dark:text-white" data-testid="promise-page">
      <div className="aurora opacity-40" />
      <div className="grain" />
      <PublicNav />

      {/* Hero */}
      <section className="relative">
        <div className="mx-auto max-w-[720px] px-6 pt-20 pb-10 sm:px-8 sm:pt-28">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <div className="text-eyebrow text-indigo-600 dark:text-indigo-300" data-testid="promise-eyebrow">Our promise</div>
            <h1 className="font-display mt-3 text-5xl font-light leading-[1.1] tracking-tight sm:text-6xl" data-testid="promise-headline">
              Our <span className="brand-text">Promise</span>.
            </h1>
            <p className="mt-6 text-lg italic leading-relaxed text-slate-600 dark:text-white/65" data-testid="promise-subhead">
              A few things we want to be honest with you about — written by the founder.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Commitments */}
      <section className="relative">
        <div className="mx-auto max-w-[720px] px-6 pb-16 sm:px-8">
          <div className="space-y-14" data-testid="promise-commitments">
            {COMMITMENTS.map((c, idx) => (
              <motion.div
                key={c.heading}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: idx * 0.05, duration: 0.6, ease: EASE }}
                data-testid={`promise-commitment-${idx}`}
              >
                <h2 className="font-display text-2xl font-medium tracking-tight text-slate-900 dark:text-white sm:text-3xl">
                  {c.heading}
                </h2>
                <p className="mt-3 text-lg leading-[1.75] text-slate-700 dark:text-white/75">
                  {c.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder note */}
      <section className="relative">
        <div className="mx-auto max-w-[720px] px-6 pb-12 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: EASE }}
            className="border-t border-slate-900/8 pt-10 dark:border-white/10"
            data-testid="promise-founder-note"
          >
            <p className="text-sm italic leading-[1.8] text-slate-500 dark:text-white/55">
              I built this company because I was tired of how the software industry treats small business owners.
              Threatening emails when a card fails. &ldquo;Implementation fees&rdquo; that double the cost. Auto-renewals that
              lock you in for a year. That&rsquo;s not how we do business here.
            </p>
            <p className="mt-4 text-sm italic text-slate-500 dark:text-white/55">
              &mdash; The Symplee team
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTAs */}
      <section className="relative">
        <div className="mx-auto max-w-[720px] px-6 pb-32 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: EASE }}
            className="flex flex-col items-start gap-4"
          >
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/signup" data-testid="promise-cta-primary">
                <Button className="h-12 rounded-full bg-indigo-600 px-7 text-base text-white shadow-sm hover:bg-indigo-700 dark:bg-white dark:text-slate-900 dark:hover:bg-zinc-200">
                  Start your 7-day free trial <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
              <Link
                to="/about"
                className="font-display text-base text-slate-700 underline-offset-4 hover:underline dark:text-white/80"
                data-testid="promise-cta-secondary"
              >
                Read more about us &rarr;
              </Link>
            </div>
            <p className="mt-2 text-sm text-slate-500 dark:text-white/55" data-testid="promise-contact-line">
              Questions? Email{" "}
              <a
                href="mailto:info@symplee.ai"
                className="text-slate-700 underline-offset-4 hover:underline dark:text-white/80"
              >
                info@symplee.ai
              </a>{" "}
              &mdash; a real human reads every message.
            </p>
            <p
              className="mt-10 text-sm italic text-slate-500 dark:text-white/45"
              data-testid="promise-portfolio-line"
            >
              Symplee is an Automation Prime portfolio company.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
